import MessageHeader from "./MessageHeader";
import {
  ChatContext,
  ChatMessagesContext,
  ForwardMessageProvider,
  MessagePanelProvider,
  OrganizationContext,
  RecipientInfoProvider,
  ReplyMessageProvider,
  UnSupportedChatProvider,
  UploadProgress,
  useTranslation,
} from "hhn-commons";
import { MessagePanel } from "./MessagePanel";
import RecipientInfo from "./messagePanelMenu/recipientInfo/RecipientInfo";
import { useCallback, useContext } from "react";
import { Typography } from "@mui/material";
import { CaptionWrapper } from "./MessagePanel.style";
import ForwardMessagesActionPanel from "./messagePanelMenu/forwardTo/ForwardMessagesActionPanel";
import { InputMessage } from "./inputMessageBox/InputMessage";
import {
  IEditMessagePayload,
  IFileAndCaption,
  ISendMessagePayload,
  SEND_MESSAGE_TYPE,
} from "@home-health-notify/domain";
import { userTracking } from "../analytics/userTracking";
import { ISubmitSocketResponse } from "@home-health-notify/models";

type MessageListViewProps = {
  onSearchFocus: () => void;
  fileProgressStatus?: UploadProgress;
  isUploadFailed: boolean;
  openFileDialog: () => void;
  onPasteFile: (fileList: IFileAndCaption[]) => Promise<void>;
};

const MessageListView = ({
  onSearchFocus,
  fileProgressStatus,
  isUploadFailed,
  openFileDialog,
  onPasteFile,
}: MessageListViewProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { scrollMessages, isMessageLoading } = useContext(ChatMessagesContext);

  const { chat, isChatLoading, isChatDeleted } = useContext(ChatContext);

  const NoOp = () => {};

  const sendMessageWithTracking = useCallback(
    (payload: ISendMessagePayload, type: SEND_MESSAGE_TYPE) => {
      if (!chat) return;

      const trackingId = `msg_${Date.now()}`;
      userTracking.trackMessageSendStart(trackingId);

      chat.sendMessage(payload, type, (responseData, socketPayload) => {
        const chatType = socketPayload?.data?.chatType;
        const messageType = socketPayload?.data?.contentType;

        if (!responseData.statusCode || responseData.statusCode === 200) {
          userTracking.trackMessageSendComplete(
            trackingId,
            chatType,
            messageType,
          );
        } else {
          userTracking.trackMessageSendFailed(
            trackingId,
            `status_${responseData.statusCode}`,
            chatType,
          );
        }
      });
    },
    [chat],
  );

  const editMessageWithTracking = useCallback(
    (
      payload: IEditMessagePayload,
      responseCallback?: (
        data: ISubmitSocketResponse,
        payload?: IEditMessagePayload,
      ) => void,
      onTimeout?: () => void,
    ) => {
      if (!chat) return;

      const trackingId = `edit_${Date.now()}`;
      const chatType = chat.getChatType();

      userTracking.trackMessageEditStart(trackingId);

      chat.editMessage(
        payload,
        (responseData, socketPayload) => {
          if (!responseData.statusCode || responseData.statusCode === 200) {
            userTracking.trackMessageEditComplete(trackingId, chatType);
          } else {
            userTracking.trackMessageEditFailed(
              trackingId,
              `status_${responseData.statusCode}`,
              chatType,
            );
          }

          responseCallback?.(responseData, socketPayload);
        },
        () => {
          userTracking.trackMessageEditFailed(trackingId, "timeout", chatType);

          onTimeout?.();
        },
      );
    },
    [chat],
  );

  return (
    <>
      {!!chat && (
        <UnSupportedChatProvider>
          <MessageHeader
            chatAccess={chat}
            showSearch={false}
            setShowSearch={onSearchFocus}
            onFilter={NoOp}
            isMemberFlag={chat?.isMember()}
            isChatLoading={isChatLoading}
          />
          <MessagePanelProvider>
            <ForwardMessageProvider>
              <RecipientInfoProvider>
                <ReplyMessageProvider>
                  {selectedOrgUser && (
                    <MessagePanel
                      chatAccess={chat}
                      selectedOrgUser={selectedOrgUser}
                      progressData={fileProgressStatus}
                      isUploadFailed={isUploadFailed}
                      scrollMessages={scrollMessages}
                      isMessageLoading={isMessageLoading}
                    />
                  )}
                  {isChatDeleted && (
                    <CaptionWrapper data-testid="InputCaption">
                      <Typography style={{ fontWeight: 500 }} variant={"body1"}>
                        {tNs("empty.deletedChatMessage")}
                      </Typography>
                    </CaptionWrapper>
                  )}
                  {!isChatDeleted &&
                    (chat?.isPrivate() || chat?.isMember()) && (
                      <InputMessage
                        chat={chat}
                        sendMessage={sendMessageWithTracking}
                        editMessage={editMessageWithTracking}
                        selectFile={openFileDialog}
                        onPasteFile={onPasteFile}
                      />
                    )}
                  {!isChatDeleted &&
                    !chat?.isPrivate() &&
                    chat?.isMember() === false && (
                      <CaptionWrapper data-testid="InputCaption">
                        <Typography
                          style={{ fontWeight: 500 }}
                          variant={"body1"}
                        >
                          {tNs("empty.removedGroupMessage")}
                        </Typography>
                      </CaptionWrapper>
                    )}
                  <ForwardMessagesActionPanel />
                  <RecipientInfo chatInfoAccess={chat} />
                </ReplyMessageProvider>
              </RecipientInfoProvider>
            </ForwardMessageProvider>
          </MessagePanelProvider>
        </UnSupportedChatProvider>
      )}
    </>
  );
};

export default MessageListView;
